import { Divider, Paper } from '@mui/material';
import { PriceSuggestionType } from '@retail/products/types';
import { useCallback } from 'react';
import { SalesPriceConditionPrioOptions } from './SalesPriceConditionPrioOptions';
import { SalesPriceConditionPriority } from './SalesPriceConditionPriority';
import { MarkupBinding } from '@retail/calculus/types';

interface Props {
  priceSuggestionPriority: PriceSuggestionType[];
  availablePriceSuggestions: PriceSuggestionType[];
  currentMarkup: number | null;
  includeMarkup: boolean;
  getPriceTypeValue: (priceType: PriceSuggestionType) => string;
  updatePrizeSuggestionPrioritazion: (newPrio: PriceSuggestionType[]) => void;
  markupCondition?: MarkupBinding;
  setMarkupSalesCoverageDegree: (value: string | null) => void;
  isLoadingFetchPriceSuggestions: boolean;
}

export function SalesPriceConditionPrioritization({
  priceSuggestionPriority,
  availablePriceSuggestions,
  currentMarkup,
  includeMarkup,
  markupCondition,
  isLoadingFetchPriceSuggestions,
  updatePrizeSuggestionPrioritazion,
  getPriceTypeValue,
  setMarkupSalesCoverageDegree,
}: Props) {
  const handlePriceSuggestionPrioritization = useCallback(
    (value: PriceSuggestionType) => {
      updatePrizeSuggestionPrioritazion(
        priceSuggestionPriority.includes(value)
          ? priceSuggestionPriority.filter((priority) => priority !== value)
          : priceSuggestionPriority.includes('MARKUP')
          ? [...priceSuggestionPriority.slice(0, -1), value, 'MARKUP']
          : [...priceSuggestionPriority, value]
      );
    },
    [priceSuggestionPriority, updatePrizeSuggestionPrioritazion]
  );

  const handleReprioritizedPriceSuggestions = useCallback(
    (priceTypeIndex: number, prioritize: 'UP' | 'DOWN') => {
      const newIndex = prioritize === 'UP' ? priceTypeIndex - 1 : priceTypeIndex + 1;
      const newPrioritazion = [...priceSuggestionPriority];
      [newPrioritazion[priceTypeIndex], newPrioritazion[newIndex]] = [
        newPrioritazion[newIndex],
        newPrioritazion[priceTypeIndex],
      ];
      updatePrizeSuggestionPrioritazion(newPrioritazion);
    },
    [priceSuggestionPriority, updatePrizeSuggestionPrioritazion]
  );
  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'grid',
        gridTemplateColumns: '50% 1px 50%',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <SalesPriceConditionPriority
        selectedPriceSuggestionTypes={priceSuggestionPriority}
        markupCondition={markupCondition}
        currentMarkup={currentMarkup}
        checkPriceType={handlePriceSuggestionPrioritization}
        handleReprioritizedPriceSuggestions={handleReprioritizedPriceSuggestions}
        getPriceTypeValue={getPriceTypeValue}
        setMarkupSalesCoverageDegree={setMarkupSalesCoverageDegree}
      />

      <Divider orientation="vertical" sx={{ height: 'auto' }} />

      <SalesPriceConditionPrioOptions
        priceSuggestionTypes={availablePriceSuggestions}
        checkedPriceTypes={priceSuggestionPriority}
        includeMarkup={includeMarkup}
        isLoadingFetchPriceSuggestions={isLoadingFetchPriceSuggestions}
        checkPriceType={handlePriceSuggestionPrioritization}
        getPriceTypeValue={getPriceTypeValue}
      />
    </Paper>
  );
}
